import { nanoid } from '@reduxjs/toolkit'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { graphqlApi } from 'src/redux/api/graphql/graphqlApi'
import type { ReduxClients, ReduxUserInfo } from 'src/types/apiType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { filteredArrayItemsFromUserInfo } from 'src/utils/fetchUtils'
import { makeErrorToast, msg } from 'src/utils/makeToast'
import { getProperUpdateValues } from 'src/utils/updateApiUtil'

export const clientApi = graphqlApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchClientsByTenantId: builder.query({
			query: ({
				tenantId,
				userInfo,
			}: {
				tenantId: string
				userInfo: ReduxUserInfo
			}) => ({
				queryName: 'clientsByTenantId',
				data: {
					tenantId,
					filter: { activeFlag: { eq: true } },
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (
				response: ReduxClients[],
				meta,
				{ userInfo }
			) => {
				const filteredResponse = filteredArrayItemsFromUserInfo({
					items: response,
					userInfo,
				})
				return filteredResponse as ReduxClients[]
			},
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({
								type: 'Client' as const,
								id: id as string,
							})),
							{ type: 'Client', id: 'LIST' },
						]
					: [{ type: 'Client', id: 'LIST' }],
		}),
		fetchClientsbyId: builder.query({
			query: (id: string) => ({
				queryName: 'getClients',
				data: {
					id,
				},
				isList: false,
			}),
			transformResponse: (response: ReduxClients) => {
				const typedResponse = response
				return typedResponse
			},
			providesTags: (result, error, id) => [{ type: 'Client', id }],
		}),
		createClientById: builder.mutation({
			queryFn: async ({
				clientName,
				tenantId,
				teamCount,
			}: {
				clientName: string
				tenantId: string
				teamCount: string
			}) => {
				try {
					const newClient = {
						id: nanoid(),
						name: clientName,
						tenantId: tenantId,
						teamCount: `${clientName}-${teamCount}`,
						activeFlag: true,
						offlineIds: ['dummyId'],
					} as ReduxClients
					newClient.parentTeamId = newClient.id

					await API.graphql(
						graphqlOperation(mutations.createClients, {
							input: newClient,
						})
					)
					return { data: null }
				} catch (error: any) {
					makeErrorToast(msg)
					return { error }
				}
			},
			invalidatesTags: () => [{ type: 'Client', id: 'LIST' }],
		}),
		updateClientById: builder.mutation({
			queryFn: async ({
				client,
				clientName,
				teamCount,
				userInfo,
			}: {
				client: ReduxClients
				clientName: string
				teamCount: string
				userInfo: ReduxUserInfo
			}) => {
				try {
					const {
						data: { getClients },
					} = (await API.graphql(
						graphqlOperation(queries.getClients, { id: client.id })
					)) as {
						data: { getClients: ReduxClients }
					}
					const updatedClient = getProperUpdateValues<ReduxClients>({
						updatedInput: {
							name: clientName,
							teamCount: teamCount,
						},
						originalInput: getClients,
					})
					delete updatedClient?.projects
					await API.graphql(
						graphqlOperation(mutations.updateClients, {
							input: updatedClient,
						})
					)
					return { data: null }
				} catch (error: any) {
					makeErrorToast(msg)
					return { error }
				}
			},
			async onQueryStarted(
				{ client, clientName, teamCount, userInfo },
				{ dispatch, queryFulfilled }
			) {
				const patchResult = dispatch(
					clientApi.util.updateQueryData(
						'fetchClientsByTenantId',
						{ tenantId: client.tenantId as string, userInfo },
						(draft) => {
							draft.map((item) => {
								if (item.id === client.id) {
									item.name = clientName
									item.teamCount = teamCount
								}
								return item
							})
						}
					)
				)
				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
				}
			},
		}),
		deleteClientById: builder.mutation({
			queryFn: async ({ clientId }: { clientId: string }) => {
				try {
					await API.graphql(
						graphqlOperation(mutations.deleteClientById, {
							clientId,
						})
					)
					return { data: null }
				} catch (error: any) {
					makeErrorToast(msg)
					return { error }
				}
			},
			invalidatesTags: () => [{ type: 'Client', id: 'LIST' }],
		}),
	}),
	overrideExisting: false,
})

export const {
	useFetchClientsByTenantIdQuery,
	useCreateClientByIdMutation,
	useUpdateClientByIdMutation,
	useDeleteClientByIdMutation,
} = clientApi
